import type { GridPaginationModel, GridSortModel } from '@mui/x-data-grid'
import type { AxiosResponse } from 'axios'
import axios from 'axios'
import { DateTime } from 'luxon'

import { getCountryAsAlpha2Code } from '@/constants/country'
import environment from '@/environment'
import type { BidType, MarketProgram } from '@/features/bidding/constants'
import type { AllocatedVolumes } from '@/features/bidding/types/allocatedVolumes'
import type {
  ApiAcceptedCapacity,
  ApiCapacityOffer,
  ApiValidateConflictsPayload,
  ApiValidateConflictsResponse,
} from '@/features/bidding/types/api'
import type { ExtendedBid, Forecast, SuggestedBid } from '@/features/bidding/types/bid'
import type { BidHistoriesPage } from '@/features/bidding/types/bidHistory'
import type { BidUpdate, BidUpdateOverviewItem } from '@/features/bidding/types/bidUpdate'
import type { CapacityObligations } from '@/features/bidding/types/capacityObligations'
import type { MarketDate } from '@/features/bidding/utils/date/marketDate'
import {
  allocatedVolumesWithLuxonDates,
  bidHistoryWithLuxonDates,
  capacityObligationsWithLuxonDates,
  extendedBidToApiAcceptedCapacityOffer,
  extendedBidToApiCapacityOffer,
  extendedBidWithContextWithLuxonDates,
  forecastWithLuxonDates,
  toInternalBidHistory,
  toInternalBidUpdate,
  toInternalBidUpdateOverviewItem,
  toInternalExtendedBid,
  toInternalForecast,
  toSuggestedBidWithLuxonDates,
} from '@/features/bidding/utils/model/modelConversion'
import {
  buildPriceAllocationRequest,
  toPriceAllocationResponse,
} from '@/features/bidding/utils/model/priceAllocationConversion'

export const PORTFOLIO_MANAGER_API_URL = environment.services.portfolioManagerApiUrl

export const GET_BID_API_ID = 'GET_BID'
export const GET_LATEST_BID_API_ID = 'GET_LATEST_BID'
export const GET_BID_HISTORIES_API_ID = 'GET_BID_HISTORIES'
export const GET_BID_UPDATE_OVERVIEW_API_ID = 'GET_BID_UPDATE_OVERVIEW'
export const GET_BID_UPDATES_API_ID = 'GET_BID_UPDATES'

export const GET_FORECAST_API_ID = 'GET_FORECAST'
export const GET_BIDS_API_STALE_TIME = 1000 * 60 * 60 * 24 // 24 hours

export const saveExtendedBids = async (bids: ExtendedBid[]) => {
  const body = bids.map((bid) => extendedBidToApiCapacityOffer(bid))
  return axios.post<ApiCapacityOffer, string>(`${PORTFOLIO_MANAGER_API_URL}/bid/capacity/bulk`, body)
}

export const acceptExtendedBid = async (bid: ExtendedBid) => acceptExtendedBids([bid])

export const acceptExtendedBids = async (bids: ExtendedBid[]) => {
  const body = bids.map((bid) => extendedBidToApiAcceptedCapacityOffer(bid))
  return axios.post<ApiAcceptedCapacity, string>(`${PORTFOLIO_MANAGER_API_URL}/bid/accepted-capacity/bulk`, body)
}

export const priceAllocation = async (
  aggregatedBid: any,
  activationGroupBids: ExtendedBid[],
): Promise<ExtendedBid[]> => {
  const requestBody = buildPriceAllocationRequest(aggregatedBid, activationGroupBids)
  const response = await axios.post(`${PORTFOLIO_MANAGER_API_URL}/bid/price-allocation`, requestBody)
  return toPriceAllocationResponse(response.data, activationGroupBids)
}

const findLatestBidAt = async (
  path: string,
  deliveryDay: MarketDate,
  activationGroupUuid: string,
  bidType: BidType,
): Promise<ExtendedBid | null> => {
  const params = {
    deliveryDay: deliveryDay.toISODate(),
    activationGroupUuid,
    bidType,
  }

  return await axios
    .get(`${PORTFOLIO_MANAGER_API_URL}${path}`, { params })
    .then((response) => {
      const bid = toInternalExtendedBid(extendedBidWithContextWithLuxonDates(response.data))
      return bid.ptus.length > 0 ? bid : null
    })
    .catch((error) => {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        return null
      }
      throw error
    })
}

export const findLatestBid = (
  deliveryDay: MarketDate,
  activationGroupUuid: string,
  bidType: BidType,
): Promise<ExtendedBid | null> => findLatestBidAt('/bid-schedule/latest', deliveryDay, activationGroupUuid, bidType)

export const findLatestOfferedBid = (
  deliveryDay: MarketDate,
  activationGroupUuid: string,
  bidType: BidType,
): Promise<ExtendedBid | null> =>
  findLatestBidAt('/bid-schedule/offered/latest', deliveryDay, activationGroupUuid, bidType)

export const findLatestAcceptedBid = (
  deliveryDay: MarketDate,
  activationGroupUuid: string,
  bidType: BidType,
): Promise<ExtendedBid | null> =>
  findLatestBidAt('/bid-schedule/accepted/latest', deliveryDay, activationGroupUuid, bidType)

export const findForecast = async (date: MarketDate, activationGroupUuid: string): Promise<Forecast | null> => {
  const response = await axios
    .get(`${PORTFOLIO_MANAGER_API_URL}/forecast`, {
      params: {
        date: date.toISODate(),
        activationGroupUuid,
      },
    })
    .then((response) =>
      response.data
        .map((forecast: any) => forecastWithLuxonDates(forecast))
        .map((forecast: any) => toInternalForecast(forecast)),
    )
    .catch((error) => {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        return null // Not found
      }
      throw error
    })

  if (!response || response.length === 0) {
    return null
  }

  return response[0]
}

export const getBidHistories = async (
  pagination: GridPaginationModel,
  sorting: GridSortModel,
  filter: BidHistoriesFilter,
  country: CountryIdentifier,
): Promise<BidHistoriesPage> => {
  const response = await axios.get(`${PORTFOLIO_MANAGER_API_URL}/bid-history`, {
    params: buildBidHistoriesParams(pagination, sorting, filter, country),
  })
  return {
    bidHistories: response.data.bidHistories
      .map((bidHistory: any) => bidHistoryWithLuxonDates(bidHistory))
      .map((bidHistory: any) => toInternalBidHistory(bidHistory)),
    currentPage: response.data.currentPage,
    totalBidHistories: response.data.totalBidHistories,
  }
}

/**
 * `GET /bid-update/overview` — returns one entry per activation group that has at least one bid
 * update for the given portfolio, market program, delivery day and bid type, together with the
 * number of bid updates it has. Used to populate the activation group selector without fetching
 * the updates themselves.
 */
export const getBidUpdateOverview = async (
  portfolioCode: string,
  marketProgram: MarketProgram,
  deliveryDay: MarketDate,
  bidType: BidType,
): Promise<BidUpdateOverviewItem[]> => {
  const response = await axios.get(`${PORTFOLIO_MANAGER_API_URL}/bid-update/overview`, {
    params: {
      portfolioCode,
      marketProgram,
      deliveryDay: deliveryDay.toISODate(),
      bidType,
    },
  })
  return response.data.map((item: any) => toInternalBidUpdateOverviewItem(item))
}

/**
 * `GET /bid-update` — returns the bid updates for a single activation group, delivery day and
 * bid type. PTUs may be incomplete (fewer than the full 24 hours).
 */
export const getBidUpdates = async (
  activationGroupUuid: string,
  deliveryDay: MarketDate,
  bidType: BidType,
): Promise<BidUpdate[]> => {
  const response = await axios.get(`${PORTFOLIO_MANAGER_API_URL}/bid-update`, {
    params: {
      activationGroupUuid,
      deliveryDay: deliveryDay.toISODate(),
      bidType,
    },
  })
  return response.data.map((bidUpdate: any) => toInternalBidUpdate(bidUpdate))
}

export type BidHistoriesFilter = {
  portfolioCode?: string
  marketProgram?: MarketProgram
  activationGroupUuid?: string
  deliveryDay?: MarketDate
  fromDeliveryDay?: MarketDate
  bidType?: BidType
}

const buildBidHistoriesParams = (
  pagination: GridPaginationModel,
  sorting: GridSortModel,
  filter: BidHistoriesFilter,
  country: CountryIdentifier,
) => {
  const params = new URLSearchParams()

  params.append('page', String(pagination.page))
  params.append('pageSize', String(pagination.pageSize))
  params.append('countryCode', getCountryAsAlpha2Code(country))

  for (const sortItem of sorting) params.append('sortItems', JSON.stringify(sortItem))

  if (filter.portfolioCode) params.append('portfolioCode', filter.portfolioCode)
  if (filter.marketProgram) params.append('marketProgram', filter.marketProgram)
  if (filter.activationGroupUuid) params.append('activationGroupUuid', filter.activationGroupUuid)
  if (filter.deliveryDay) params.append('deliveryDay', filter.deliveryDay.toISODate())
  if (filter.fromDeliveryDay) params.append('fromDeliveryDay', filter.fromDeliveryDay.toISODate())
  if (filter.bidType) params.append('bidType', filter.bidType)

  return params
}

export const findSuggestedBid = async (
  startDate: MarketDate,
  endDate: MarketDate,
  activationGroupUuid: string,
): Promise<SuggestedBid[]> => {
  try {
    const response = await axios.get(`${PORTFOLIO_MANAGER_API_URL}/suggested-bids/${activationGroupUuid}`, {
      params: {
        startDate: startDate.getStartOfDay(),
        endDate: endDate.getEndOfDay(),
      },
    })

    return response.data
      .map(toSuggestedBidWithLuxonDates)
      .sort((bid1: SuggestedBid, bid2: SuggestedBid) => bid1.ptu.start.valueOf() - bid2.ptu.start.valueOf())
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return []
    }
    throw error
  }
}

export const importAcceptedBid = async (tsv: string) => {
  return axios.post(`${PORTFOLIO_MANAGER_API_URL}/bid/import/aggregated-accepted-bid`, tsv, {
    headers: {
      'Content-Type': 'text/plain',
    },
  })
}

export const importIntradayEnergyOffer = async (tsv: string) => {
  return axios.post(`${PORTFOLIO_MANAGER_API_URL}/bid/import/tsv`, tsv, {
    headers: {
      'Content-Type': 'text/plain',
    },
  })
}

export const getBidExportsIntraday = async (
  portfolioCode: string,
  marketProgram: string,
  deliveryDay: string,
  bidType: string,
): Promise<AxiosResponse<string>> => {
  return axios.get(`${PORTFOLIO_MANAGER_API_URL}/bid/exports/intraday`, {
    params: { portfolioCode, marketProgram, deliveryDay, bidType },
  })
}

export const getMfrrEnergyBid = async (countryCode: string, deliveryDay: string): Promise<AxiosResponse<string>> => {
  return axios.get(`${PORTFOLIO_MANAGER_API_URL}/bid/exports/mfrr/energy`, {
    params: {
      countryCode,
      deliveryDay,
    },
  })
}

export const getMfrrCapacityBid = async (countryCode: string, deliveryDay: string): Promise<AxiosResponse<Blob>> => {
  return axios.get(`${PORTFOLIO_MANAGER_API_URL}/bid/exports/mfrr/capacity`, {
    params: {
      countryCode,
      deliveryDay,
    },
    responseType: 'blob',
  })
}

export const validateConflicts = async (
  payload: ApiValidateConflictsPayload,
): Promise<ApiValidateConflictsResponse> => {
  const response = await axios.post(`${PORTFOLIO_MANAGER_API_URL}/bid/validate/conflicts`, payload)
  return {
    ...response.data,
    issues: response.data.issues.map((issue) => {
      return {
        ...issue,
        conflicts: issue.conflicts.map((conflict) => {
          return {
            ...conflict,
            ptu: {
              start: DateTime.fromISO(conflict.ptu.start),
              end: DateTime.fromISO(conflict.ptu.end),
            },
          }
        }),
      }
    }),
  }
}

export const findCapacityObligations = async (
  marketProgram: MarketProgram,
  portfolioCode: string,
  date: MarketDate,
): Promise<CapacityObligations | null> => {
  const response = await axios
    .get(`${PORTFOLIO_MANAGER_API_URL}/capacity-obligations`, {
      params: {
        marketProgram,
        portfolioCode,
        deliveryDay: date.toISODate(),
      },
    })
    .then((response) => capacityObligationsWithLuxonDates(response.data))
    .catch((error) => {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        return null // Not found
      }
      throw error
    })

  if (!response) {
    return null
  }

  return response
}

export const findCommitments = async (date: MarketDate, portfolioCode: string): Promise<AllocatedVolumes | null> => {
  const response = await axios
    .get(`${PORTFOLIO_MANAGER_API_URL}/allocated-volumes`, {
      params: {
        deliveryDay: date.toISODate(),
        portfolioCode,
      },
    })
    .then((response) => allocatedVolumesWithLuxonDates(response.data))
    .catch((error) => {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        return null // Not found
      }
      throw error
    })

  if (!response) {
    return null
  }

  return response
}
